import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { db, collection, query, getDocs } from '../firebaseConfig';
import { Line } from 'react-chartjs-2';
import Card from '@mui/material/Card';
import MDBox from '../components/MDBox';
import Divider from '@mui/material/Divider';
import AssessmentIcon from '@mui/icons-material/Assessment';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import MDTypography from '../components/MDTypography';
import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePeriod } from '../components/PeriodContext';

const TeamScoreGraph = ({ userID }) => {
  const { selectedPeriod, selectedYear, selectedMonth } = usePeriod();
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  // Fetch scores from both Users/{userID}/monthlyScores and tradeAccounts
  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      try {
        let allScores = [];

        // 1. Fetch overall scores from Users/{userID}/monthlyScores
        const overallScoresPath = `Users/${userID}/monthlyScores`;
        const overallScoresQuery = query(collection(db, overallScoresPath));
        const overallScoreSnapshot = await getDocs(overallScoresQuery);
        const overallScoresData = overallScoreSnapshot.docs.map((doc) => ({
          id: doc.id,
          source: 'overall',
          month: doc.data().month instanceof Object ? doc.data().month.toDate() : new Date(doc.data().month),
          score: Number(doc.data().score) || 0,
        }));
        console.log(`TeamScoreGraph: Overall scores for userID ${userID}:`, overallScoresData);
        allScores = [...allScores, ...overallScoresData];

        // 2. Fetch per-account scores from tradeAccounts
        const accountsQuery = query(collection(db, `Users/${userID}/tradeAccounts`));
        const accountsDocs = await getDocs(accountsQuery);
        const tradeAccountIds = accountsDocs.docs.map((doc) => doc.id).concat('default');

        for (const accountId of tradeAccountIds) {
          const scoresPath = `Users/${userID}/tradeAccounts/${accountId}/monthlyScores`;
          const scoresQuery = query(collection(db, scoresPath));
          const scoreSnapshot = await getDocs(scoresQuery);
          const scoresData = scoreSnapshot.docs.map((doc) => ({
            id: doc.id,
            source: `account:${accountId}`,
            month: doc.data().month instanceof Object ? doc.data().month.toDate() : new Date(doc.data().month),
            score: Number(doc.data().score) || 0,
          }));
          console.log(`TeamScoreGraph: Scores for userID ${userID}, account ${accountId}:`, scoresData);
          allScores = [...allScores, ...scoresData];
        }

        // Aggregate scores by month (average duplicates)
        const aggregatedScores = {};
        allScores.forEach((score) => {
          const monthKey = score.month.toISOString().slice(0, 7); // YYYY-MM
          if (!aggregatedScores[monthKey]) {
            aggregatedScores[monthKey] = { totalScore: 0, count: 0, scores: [] };
          }
          aggregatedScores[monthKey].totalScore += score.score;
          aggregatedScores[monthKey].count += 1;
          aggregatedScores[monthKey].scores.push({ source: score.source, score: score.score });
        });

        const averagedScores = Object.entries(aggregatedScores).map(([month, data]) => ({
          month: new Date(month),
          score: data.totalScore / data.count,
        }));

        // Log March 2025 scores specifically
        const march2025Scores = aggregatedScores['2025-03'];
        if (march2025Scores) {
          console.log('TeamScoreGraph: March 2025 scores:', {
            individualScores: march2025Scores.scores,
            averageScore: march2025Scores.totalScore / march2025Scores.count,
            count: march2025Scores.count,
          });
        }

        console.log('TeamScoreGraph: Aggregated scores:', averagedScores);
        setScores(averagedScores);
        setHasData(averagedScores.length > 0);
      } catch (error) {
        console.error('TeamScoreGraph: Error fetching scores:', error);
        setScores([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    if (userID) {
      fetchScores();
    }
  }, [userID]);

  const filterScores = useMemo(() => {
    const filtered = scores
      .filter((score) => {
        const date = new Date(score.month);
        if (selectedPeriod === 'this-month') {
          return date.getMonth() === new Date().getMonth() && date.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'this-year') {
          return date.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return date.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return date.getMonth() + 1 === selectedMonth && date.getFullYear() === selectedYear;
        }
        return true; // all-time, today, this-week show all scores
      })
      .sort((a, b) => new Date(a.month) - new Date(b.month));

    console.log('TeamScoreGraph: Filtered scores:', filtered);
    return filtered;
  }, [scores, selectedPeriod, selectedYear, selectedMonth]);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { year: '2-digit', month: 'short' });
  };

  const data = {
    labels: filterScores.map((score) => formatDate(score.month)),
    datasets: [
      {
        label: 'Monthly Score',
        data: filterScores.map((score) => score.score),
        fill: false,
        backgroundColor: 'rgb(75, 192, 192)',
        borderColor: 'rgba(75, 192, 192, 0.2)',
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        type: 'category',
        ticks: {
          autoSkip: true,
          maxTicksLimit: 12,
          maxRotation: 45,
          minRotation: 45,
        },
      },
      y: {
        beginAtZero: true,
        min: 0,
        max: 10,
        grid: { display: true },
        ticks: { stepSize: 1 },
      },
    },
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: (context) => `Score: ${context.parsed.y.toFixed(1)}`,
        },
      },
    },
  };

  const tooltipContent = `FX Bullsheet Score measures trading performance (0-10) across all accounts and overall. Combines win rate, profit consistency, risk management, and efficiency metrics. 8+ indicates excellent trading strategy, 5-7 shows good performance, 3-4 is average, 1-2 is poor.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }
    if (!hasData) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No score data available
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your score analysis
          </MDTypography>
        </MDBox>
      );
    }
    return (
      <MDBox height="100%">
        <Line data={data} options={chartOptions} />
      </MDBox>
    );
  };

  return (
    <Card style={{ minHeight: '300px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <AssessmentIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Monthly Score Progress (All Accounts & Overall)
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': { color: theme.palette.background.paper },
                  },
                },
              }}
            >
              <InfoIcon sx={{ ml: 1, fontSize: '1rem', color: iconColor, cursor: 'help' }} />
            </Tooltip>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamScoreGraph.propTypes = {
  userID: PropTypes.string.isRequired,
};

export default TeamScoreGraph;